import {
  computed,
  ref,
  shallowRef,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
} from 'vue';

import type { SeoDescriptor, SeoOverride, TitleNotification } from './types';

/** @public */
type SeoController = {
  clearRoute: () => void;
  registerOverride: (override: MaybeRefOrGetter<SeoOverride>) => () => void;
  resolved: ComputedRef<SeoDescriptor | null>;
  setRoute: (descriptor: SeoDescriptor) => void;
  setTitleNotification: (notification: MaybeRefOrGetter<TitleNotification | null>) => () => void;
};

type SeoOverrideLayer = { value: SeoOverride };

const createSeoController = (): SeoController => {
  const route = ref<SeoDescriptor | null>(null);
  const overrides = shallowRef<SeoOverrideLayer[]>([]);
  const notification = ref<TitleNotification | null>(null);

  const resolved = computed<SeoDescriptor | null>(() => {
    if (route.value === null) {
      return null;
    }

    const override = overrides.value.reduce<SeoOverride>(
      (result: SeoOverride, item: SeoOverrideLayer): SeoOverride => ({ ...result, ...item.value }),
      {},
    );
    const descriptor: SeoDescriptor = { ...route.value, ...override };

    return notification.value === null
      ? descriptor
      : { ...descriptor, title: notification.value.title };
  });

  const setRoute = (descriptor: SeoDescriptor): void => {
    route.value = descriptor;
  };

  const clearRoute = (): void => {
    route.value = null;
  };

  const registerOverride = (override: MaybeRefOrGetter<SeoOverride>): (() => void) => {
    const layer: SeoOverrideLayer = { value: toValue(override) };
    overrides.value = [...overrides.value, layer];
    const stop = watch(
      () => toValue(override),
      (nextOverride: SeoOverride): void => {
        const index: number = overrides.value.indexOf(layer);
        layer.value = nextOverride;

        if (index !== -1) {
          overrides.value = [...overrides.value];
        }
      },
      { flush: 'sync' },
    );

    return (): void => {
      stop();
      overrides.value = overrides.value.filter((item: SeoOverrideLayer): boolean => item !== layer);
    };
  };

  const setTitleNotification = (
    nextNotification: MaybeRefOrGetter<TitleNotification | null>,
  ): (() => void) => {
    const stop = watch(
      () => toValue(nextNotification),
      (value: TitleNotification | null): void => {
        notification.value = value;
      },
      { immediate: true },
    );

    return (): void => {
      stop();
      notification.value = null;
    };
  };

  return { clearRoute, registerOverride, resolved, setRoute, setTitleNotification };
};

const seoController: SeoController = createSeoController();

export { createSeoController, seoController, type SeoController };
